/*!
 * EXPLOIT reader — server-rendered reading surface (reader platform v1).
 *
 * Renders, into the cached page for instant first paint (G-9), the full-book
 * MANIFEST (a collapsible table of contents showing all 3 acts + every chapter
 * title + the epilogue) and the OPENING sample content unit's blocks inline —
 * the PROLOGUE by default (the first real story section), not the front matter
 * (Dedications / Author's Note), which stay reachable via the TOC and the back
 * arrow. The client (app/public/src/egt.reader.js) then shows ONE chapter at a
 * time, fetching each SAMPLE unit from GET /api/reader/exploit/:chapterId as the
 * reader moves with the bottom prev/next arrows or the TOC, restores the saved
 * place (resume wins over the default open), and drives the typography controls.
 *
 * Content comes from app/data/reader/exploit/ (generated by scripts/build-reader.js;
 * app/data is gitignored, so it must be generated on deploy). If it isn't present
 * at BUILD time this element renders an honest placeholder rather than crashing.
 *
 * Brand non-negotiables (agents.md §4): frosted-glass chrome over UNDIMMED art —
 * NO scrim; true black; scarce gold; Playfair (titles + body) + Inter (the
 * "Chapter N" eyebrow + UI); rem only. Body gets a comfortable reading setup
 * (larger size, generous line-height, ~62-66ch measure) in egt.reader.scss.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.reader",
  title: "EXPLOIT reader",
  description: "The verified-reader reflowable reader for the free opening of Exploit.",
  supportServer: true
});

var fs = require("fs");
var path = require("path");
var S = require("../helpers/egt.sections.js");

/* Server-only path, guarded: this file is also bundled for the browser (client
 * registry), where webpack stubs `path` as an empty object and a load-time
 * path.join throws — which used to drop the WHOLE app client registry. */
var DATA_DIR = (path && typeof path.join === "function") ? path.join(__dirname, "..", "data", "reader", "exploit") : "";

function readJSON(file) {
  try { return JSON.parse(fs.readFileSync(path.join(DATA_DIR, file), "utf8")); }
  catch (e) { return null; }
}

/* ── the collapsible table of contents (all acts + titles) ────────────────── */
function tocList(manifest) {
  var items = [];
  (manifest.chapters || []).forEach(function (c) {
    if (c.type === "act") {
      items.push({ n: "li", c: "egt-reader__toc-act",
        i: [ { n: "span", c: "egt-reader__toc-act-kicker", i: c.kicker || "" },
          { n: "span", c: "egt-reader__toc-act-title", i: c.title || "" } ] });
      return;
    }
    var sample = c.tier === "sample";
    var label = c.type === "chapter"
      ? [ { n: "span", c: "egt-reader__toc-num", i: (c.number ? c.number : "") },
          { n: "span", c: "egt-reader__toc-title", i: c.title || "" } ]
      : [ { n: "span", c: "egt-reader__toc-title", i: c.title || "" } ];

    if (sample) {
      items.push({ n: "li", c: "egt-reader__toc-item",
        i: { n: "a", c: "egt-reader__toc-link", href: "#rdc-" + c.id,
          "data-reader-goto": c.id, i: label } });
    } else {
      // Locked: TITLE only, greyed, NOT navigable (no href), with an honest lock.
      items.push({ n: "li", c: "egt-reader__toc-item egt-reader__toc-item--locked",
        i: { n: "span", c: "egt-reader__toc-link egt-reader__toc-link--locked",
          "aria-disabled": "true",
          i: label.concat([ { n: "span", c: "egt-reader__toc-lock", "aria-hidden": "true", i: "🔒" } ]) } });
    }
  });
  return { n: "ul", c: "egt-reader__toc-list", i: items };
}

/* ── a rendered chapter section (blocks embedded as UA/JSON) ───────────────── */
function chapterSection(unit, first) {
  return { n: "section",
    c: "egt-reader__chapter" + (unit.kind === "act" ? " egt-reader__chapter--act" : "") +
       (first ? " is-first" : ""),
    id: "rdc-" + unit.chapterId,
    "data-reader-chapter": unit.chapterId,
    "data-reader-kind": unit.kind || "chapter",
    i: { n: "div", c: "egt-reader__measure", i: unit.blocks || [] } };
}

/* ── bottom chapter navigation (prev / label / next; client wires + labels) ──
 * Inert without JS. The client updates the label ("Prologue" / "Chapter 1 of 7")
 * and the disabled state on each chapter change. Front matter is reachable by
 * paging back from the Prologue. */
function chapterNav() {
  function navBtn(action, label, aria) {
    return { n: "button", c: "egt-reader__navbtn egt-reader__navbtn--" + action, type: "button",
      "data-reader-action": action, "aria-label": aria, i: label };
  }
  return { n: "nav", c: "egt-reader__nav", "aria-label": "Chapter navigation",
    i: [
      navBtn("prev", "‹ Previous", "Previous section"),
      { n: "span", c: "egt-reader__navlabel", id: "egt-reader-navlabel", "aria-live": "polite", i: "" },
      navBtn("next", "Next ›", "Next section")
    ] };
}

/* ── typography + TOC controls (inert without JS; client wires them) ─────────
 * This is the reader's own SUBMENU bar — left over from the reading surface
 * (Contents + typography). The chapter-nav arrows (‹ ☰ ›) do NOT live here; the
 * client injects those three into the MAIN site navbar instead (egt.reader.js
 * injectNavControls), so they sit up top with the logo/wordmark. */
function controls() {
  function btn(action, label, aria) {
    return { n: "button", c: "egt-reader__ctrl", type: "button",
      "data-reader-action": action, "aria-label": aria || label, i: label };
  }
  return { n: "div", c: "egt-reader__bar",
    i: [
      { n: "div", c: "egt-reader__bar-left",
        i: btn("toc", "Contents", "Show table of contents") },
      { n: "div", c: "egt-reader__bar-right",
        i: [
          btn("smaller", "A−", "Decrease text size"),
          btn("larger", "A+", "Increase text size"),
          btn("theme", "Theme", "Change reading theme"),
          btn("bookmark", "Bookmark", "Bookmark this place")
        ] }
    ] };
}

exports.render = async function render() {
  var manifest = readJSON("manifest.json");

  if (!manifest || !Array.isArray(manifest.sampleChapterIds) || !manifest.sampleChapterIds.length) {
    // Honest placeholder — the pipeline hasn't run in this build environment.
    var missing = { n: "section", c: "egt-reader egt-reader--empty",
      i: { n: "div", c: "egt__shell",
        i: [ { n: "p", c: "egt__eyebrow", i: "EXPLOIT — the reader" },
          { n: "h1", c: "egt-reader__title", i: "The reader is being prepared." },
          { n: "p", c: "egt-reader__p", i: "The opening of Exploit will be here in a moment." } ] } };
    return S.page("/exploit", [ missing ], { minimalNav: true, hideAnnounce: true });
  }

  // OPEN on the Prologue — the first real story section — not the front matter
  // (Dedications / Author's Note), which remain reachable via the TOC + back
  // arrow. A returning reader's saved place still wins (client-side resume); this
  // is only the DEFAULT first paint. Fall back to the first sample id if the
  // prologue id isn't present (defensive — the manifest always has it today).
  var sampleSet = {};
  manifest.sampleChapterIds.forEach(function (id) { sampleSet[id] = true; });
  var openId = sampleSet["pre-prologue"] ? "pre-prologue" : manifest.sampleChapterIds[0];
  var firstUnit = readJSON(openId + ".json");

  var reader = { n: "div", c: "egt-reader", "data-reader-root": "true",
    "data-reader-work": manifest.bookId || "exploit",
    "data-reader-version": manifest.contentVersion || "",
    "data-reader-rev": String(manifest.manuscriptRev || 1),
    "data-reader-sample": JSON.stringify(manifest.sampleChapterIds),
    "data-reader-open": openId,
    i: [
      controls(),
      { n: "div", c: "egt-reader__layout",
        i: [
          // Collapsible TOC (sticky on desktop; a drawer on mobile — the client
          // toggles [data-reader-toc-open] on the root).
          { n: "aside", c: "egt-reader__toc", id: "egt-reader-toc",
            i: [
              { n: "p", c: "egt-reader__toc-head",
                i: [ { n: "span", c: "egt-reader__toc-book", i: manifest.title || "Exploit" },
                  { n: "span", c: "egt-reader__toc-by", i: "by " + (manifest.author || "") } ] },
              { n: "p", c: "egt-reader__toc-note",
                i: "The free opening — Foreword, Prologue and Chapters 1–7. The full fifty chapters, three acts and epilogue are listed to show the whole world that's waiting." },
              tocList(manifest)
            ] },
          // The reading column: the opening unit (Prologue) is embedded for
          // instant paint; the client swaps in ONE chapter at a time as the
          // reader pages with the bottom arrows or the TOC.
          { n: "div", c: "egt-reader__reading",
            i: [
              { n: "div", c: "egt-reader__stream", id: "egt-reader-stream",
                i: (firstUnit ? [ chapterSection(firstUnit, true) ] : []) },
              chapterNav()
            ] }
        ] },
      // A subtle progress rail the client drives.
      { n: "div", c: "egt-reader__rail", "aria-hidden": "true",
        i: { n: "span", c: "egt-reader__rail-fill" } }
    ] };

  return S.page("/exploit", [ reader ], { minimalNav: true, hideAnnounce: true });
};
